import React from "react";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import {
  createTouchpointBuilderContent,
  useBuilderContent,
  useClaimVersionLock,
  useReplaceTouchpointVersion,
  useSetTouchpointVersionCache,
  type TouchpointIds,
} from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import type { EmailAttributes, LandingPageAttributes } from "hooks/api/use-touchpoint-versions";
import { normalizeValues } from "utilities/forms";
import { isLocked } from "utilities";
import { BuilderContent } from "types";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";

const touchpointVersionApi = getTouchpointVersion();

export const useTouchpointIds = (): TouchpointIds => {
  const client = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const touchpointContent = useBuilderContent();

  return {
    campaignId: currentCampaign.id,
    clientId: client.id,
    id: touchpointContent.id,
    touchpointId: currentTouchpoint.id,
  };
};

export const useSaveTouchpoint = () => {
  const touchpointContent = useBuilderContent();
  const touchpointIds = useTouchpointIds();
  const currentUser = useCurrentUser();
  const versionScope = {
    clientId: touchpointIds.clientId,
    campaignId: touchpointIds.campaignId,
    touchpointId: touchpointIds.touchpointId,
  };
  const setTouchpointVersionCache = useSetTouchpointVersionCache(versionScope);
  const replaceVersionMutation = useReplaceTouchpointVersion(versionScope);
  const claimLockMutation = useClaimVersionLock(versionScope);
  const { setIsLockingModalOpen, setSelectedVersion } = useTouchpointLocking();
  const [isLockReleaseModalOpen, setIsLockReleaseModalOpen] = React.useState(false);

  // A queued save may run several renders after it was requested (see the queue
  // below), so read the content through a ref kept current every render rather
  // than closing over the value from the render that called saveTouchpoint.
  const touchpointContentRef = React.useRef(touchpointContent);
  touchpointContentRef.current = touchpointContent;

  const findCurrentVersion = async () => {
    const dto = await touchpointVersionApi.getOneVersion({
      clientId: touchpointIds.clientId,
      campaignId: touchpointIds.campaignId,
      touchpointId: touchpointIds.touchpointId,
      id: touchpointIds.id,
    });
    return mapTouchpointVersionDTO(dto);
  };

  const runSave = async (updatedValue: Record<string, unknown>) => {
    const localContent = touchpointContentRef.current;

    const getIterableEmailSenderId = () => {
      //for cases when senderName is not defined yet OR when senders are empty in EmailType
      if (updatedValue.senderName === "mock_id_for_no_senders_defined") {
        //for Design tab when iterableEmailTypeId from Content settings tab is not defined yet
        if (!updatedValue.iterableEmailTypeId) {
          return (localContent as EmailAttributes).iterableEmailSenderId;
        } else {
          //for Content settings tab
          return null;
        }
      } else {
        return (
          //for Content settings tab
          updatedValue.senderName ||
          //for Design tab when iterableEmailTypeId from Content settings tab is not defined yet
          (localContent as EmailAttributes).iterableEmailSenderId
        );
      }
    };

    // setting iterableEmailSenderId
    updatedValue.iterableEmailSenderId = getIterableEmailSenderId();

    // setting iterableEmailTypeId
    updatedValue.iterableEmailTypeId =
      updatedValue.iterableEmailTypeId || (localContent as EmailAttributes).iterableEmailTypeId;

    //removing redundant fields not to send those to BE
    delete updatedValue.senderName;
    delete updatedValue.replyToEmail;
    delete updatedValue.senderEmail;
    const touchpoint = await findCurrentVersion();
    const serverTouchpointAttributes = createTouchpointBuilderContent(touchpoint) ?? touchpoint;
    let updatedAttributes = {
      ...normalizeValues(cloneDeep(localContent)),
      ...normalizeValues(updatedValue),
      status: serverTouchpointAttributes.status,
    } as EmailAttributes | LandingPageAttributes;

    if (isLocked(serverTouchpointAttributes as BuilderContent, currentUser)) {
      setSelectedVersion(touchpoint);
      setIsLockingModalOpen(true);

      throw new Error("Failed to save touchpoint: locked by another user");
    }

    if (serverTouchpointAttributes.lockedBy === null) {
      const hasDivergedFromServer = !isEqual(
        Object.fromEntries(
          Object.entries(normalizeValues(cloneDeep(serverTouchpointAttributes))).filter(
            ([key, _value]) => key !== "lastModifiedDate",
          ),
        ),
        Object.fromEntries(
          Object.entries(normalizeValues(cloneDeep(localContent))).filter(
            ([key, _value]) => key !== "lastModifiedDate",
          ),
        ),
      );

      if (hasDivergedFromServer) {
        setTouchpointVersionCache(serverTouchpointAttributes);
        setIsLockReleaseModalOpen(true);

        throw new Error("Failed to save touchpoint: local content diverged from the server");
      }

      await claimLockMutation.mutateAsync(touchpointIds.id);
    }

    await saveTouchpointVersion();

    async function saveTouchpointVersion() {
      const maxRetries = 3;
      let retries = 0;

      while (retries < maxRetries) {
        try {
          await replaceVersionMutation.mutateAsync({
            id: touchpointIds.id,
            attributes: updatedAttributes,
          });
          return;
        } catch (error: unknown) {
          const status =
            error instanceof Error && "response" in error
              ? (error.response as { status?: number } | undefined)?.status
              : undefined;

          switch (status) {
            case 409: {
              const newTouchpoint = await findCurrentVersion();
              setTouchpointVersionCache(newTouchpoint);
              const newServerAttributes =
                createTouchpointBuilderContent(newTouchpoint) ?? newTouchpoint;
              updatedAttributes = {
                ...normalizeValues(cloneDeep(newServerAttributes)),
                ...normalizeValues(updatedValue),
                status: newServerAttributes.status,
              } as EmailAttributes | LandingPageAttributes;
              console.error("Conflict error while saving touchpoint. Retrying...", error);
              retries++;
              const [randomValue = 0] = crypto.getRandomValues(new Uint32Array(1));
              const jitterMs = randomValue % 500;
              await new Promise((resolve) => setTimeout(resolve, 1000 * retries + jitterMs));
              break;
            }
            case 412: {
              const newTouchpoint = await findCurrentVersion();
              setTouchpointVersionCache(newTouchpoint);
              retries += 3;
              console.error(
                "precondition failed, you no longer have the lock for this touchpoint",
                error,
              );
              break;
            }
            default: {
              console.error("Failed to save touchpoint", error);
              throw error;
            }
          }
        }
      }

      throw new Error("Failed to save touchpoint after maximum retries");
    }
  };

  // Only one PUT for this version may be in flight at a time. saveTouchpoint calls
  // that arrive while a save is running are merged into a single pending payload
  // and flushed as one follow-up save once the in-flight save settles, instead of
  // firing a second overlapping PUT - the dominant source of 409s (spike 35683).
  const pendingUpdatesRef = React.useRef<Record<string, unknown>>({});
  const pendingWaitersRef = React.useRef<
    Array<{ resolve: () => void; reject: (error: unknown) => void }>
  >([]);
  const isSavingRef = React.useRef(false);

  const flushPendingSaves = () => {
    if (isSavingRef.current || pendingWaitersRef.current.length === 0) {
      return;
    }

    const batchedValue = pendingUpdatesRef.current;
    const waiters = pendingWaitersRef.current;
    pendingUpdatesRef.current = {};
    pendingWaitersRef.current = [];
    isSavingRef.current = true;

    runSave(batchedValue)
      .then(() => {
        waiters.forEach(({ resolve }) => resolve());
      })
      .catch((error) => {
        waiters.forEach(({ reject }) => reject(error));
      })
      .finally(() => {
        isSavingRef.current = false;
        flushPendingSaves();
      });
  };

  const saveTouchpoint = (updatedValue: Record<string, unknown>) =>
    new Promise<void>((resolve, reject) => {
      Object.assign(pendingUpdatesRef.current, updatedValue);
      pendingWaitersRef.current.push({ resolve, reject });
      flushPendingSaves();
    });

  return {
    saveTouchpoint,
    isLockReleaseModalOpen,
    setIsLockReleaseModalOpen,
  };
};
